import { supabase } from "@/integrations/supabase/client";
import type { ServiceType } from "@/lib/services";

/* ------------------------------------------------------------------ *
 *  Types & constants                                                  *
 * ------------------------------------------------------------------ */

export const VISIT_STATUSES = [
  "pending",
  "accepted",
  "rescheduled",
  "completed",
  "cancelled",
] as const;
export type VisitStatus = (typeof VISIT_STATUSES)[number];

export const VISIT_STATUS_LABEL: Record<VisitStatus, string> = {
  pending: "Pending",
  accepted: "Accepted",
  rescheduled: "Rescheduled",
  completed: "Completed",
  cancelled: "Cancelled",
};

export const VISIT_STATUS_CLASS: Record<VisitStatus, string> = {
  pending: "bg-brand-soft text-brown-dark",
  accepted: "bg-emerald-100 text-emerald-800",
  rescheduled: "bg-amber-100 text-amber-900",
  completed: "bg-secondary text-brown-dark",
  cancelled: "bg-muted text-muted-foreground",
};

export interface VisitRequest {
  id: string;
  listing_id: string;
  service_type: ServiceType;
  listing_title: string | null;
  customer_id: string;
  customer_name: string | null;
  customer_contact: string | null;
  partner_id: string;
  preferred_date: string;
  preferred_time: string;
  message: string | null;
  status: VisitStatus;
  partner_note: string | null;
  proposed_date: string | null;
  proposed_time: string | null;
  accepted_at: string | null;
  rescheduled_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  created_at: string;
  updated_at: string;
}

export function normalizeVisitStatus(value: string | null): VisitStatus {
  return (VISIT_STATUSES as readonly string[]).includes(value ?? "")
    ? (value as VisitStatus)
    : "pending";
}

/** Slots offered in the visit request form. */
export const VISIT_TIME_SLOTS = [
  "09:00 AM - 10:00 AM",
  "10:00 AM - 11:00 AM",
  "11:00 AM - 12:00 PM",
  "12:00 PM - 01:00 PM",
  "02:00 PM - 03:00 PM",
  "03:00 PM - 04:00 PM",
  "04:00 PM - 05:00 PM",
  "05:00 PM - 06:00 PM",
  "06:00 PM - 07:00 PM",
] as const;

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function formatVisitSlot(date: string | null, time: string | null): string {
  if (!date) return "—";
  const d = new Date(`${date}T00:00:00`);
  const label = Number.isNaN(d.getTime())
    ? date
    : d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  return time ? `${label} · ${time}` : label;
}

/** The slot that currently applies (partner's proposal wins once rescheduled). */
export function effectiveSlot(v: VisitRequest): { date: string; time: string } {
  if (v.status === "rescheduled" && v.proposed_date) {
    return { date: v.proposed_date, time: v.proposed_time ?? v.preferred_time };
  }
  return { date: v.preferred_date, time: v.preferred_time };
}

/* ------------------------------------------------------------------ *
 *  Reads                                                              *
 * ------------------------------------------------------------------ */

function mapRows(data: unknown): VisitRequest[] {
  return ((data as VisitRequest[]) ?? []).map((r) => ({
    ...r,
    status: normalizeVisitStatus(r.status),
  }));
}

export async function listPartnerVisitRequests(partnerId: string): Promise<VisitRequest[]> {
  const { data, error } = await supabase
    .from("visit_requests")
    .select("*")
    .eq("partner_id", partnerId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return mapRows(data);
}

export async function listCustomerVisitRequests(customerId: string): Promise<VisitRequest[]> {
  const { data, error } = await supabase
    .from("visit_requests")
    .select("*")
    .eq("customer_id", customerId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return mapRows(data);
}

/** Active request guard — one open visit request per listing per customer. */
export async function findActiveVisitRequest(
  customerId: string,
  listingId: string,
): Promise<VisitRequest | null> {
  const { data, error } = await supabase
    .from("visit_requests")
    .select("*")
    .eq("customer_id", customerId)
    .eq("listing_id", listingId)
    .in("status", ["pending", "accepted", "rescheduled"])
    .order("created_at", { ascending: false })
    .limit(1);
  if (error) throw error;
  return mapRows(data)[0] ?? null;
}

/* ------------------------------------------------------------------ *
 *  Writes                                                             *
 * ------------------------------------------------------------------ */

export interface NewVisitRequestInput {
  listingId: string;
  serviceType: ServiceType;
  listingTitle: string | null;
  partnerId: string;
  customerId: string;
  customerName: string;
  customerContact: string;
  preferredDate: string;
  preferredTime: string;
  message?: string;
}

/** Customer ID and Listing ID are attached automatically from the caller context. */
export async function createVisitRequest(input: NewVisitRequestInput) {
  const existing = await findActiveVisitRequest(input.customerId, input.listingId);
  if (existing) {
    throw new Error(
      "You already have an open visit request for this listing. Please wait for the partner's response.",
    );
  }
  const { error } = await supabase.from("visit_requests").insert({
    listing_id: input.listingId,
    service_type: input.serviceType,
    listing_title: input.listingTitle,
    partner_id: input.partnerId,
    customer_id: input.customerId,
    customer_name: input.customerName.trim(),
    customer_contact: input.customerContact.trim(),
    preferred_date: input.preferredDate,
    preferred_time: input.preferredTime,
    message: input.message?.trim() || null,
    status: "pending",
  });
  if (error) throw error;
}

export async function acceptVisitRequest(id: string, note?: string) {
  const { error } = await supabase
    .from("visit_requests")
    .update({
      status: "accepted",
      partner_note: note?.trim() || null,
      accepted_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}

export async function rescheduleVisitRequest(
  id: string,
  proposedDate: string,
  proposedTime: string,
  note?: string,
) {
  const { error } = await supabase
    .from("visit_requests")
    .update({
      status: "rescheduled",
      proposed_date: proposedDate,
      proposed_time: proposedTime,
      partner_note: note?.trim() || null,
      rescheduled_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}

export async function completeVisitRequest(id: string) {
  const { error } = await supabase
    .from("visit_requests")
    .update({ status: "completed", completed_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
}

export async function cancelVisitRequest(id: string, byUserId: string, note?: string) {
  const { error } = await supabase
    .from("visit_requests")
    .update({
      status: "cancelled",
      cancelled_by: byUserId,
      partner_note: note?.trim() || null,
      cancelled_at: new Date().toISOString(),
    })
    .eq("id", id);
  if (error) throw error;
}
